import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations from '../i18n/translations';

const STORAGE_KEY = 'restaflex_language';

const SUPPORTED_LANGUAGES = [
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  { code: 'en', label: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', dir: 'rtl' },
];

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGUAGES.find(l => l.code === saved) ? saved : 'tr';
  });

  // Apply dir attribute for RTL support
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === lang);
    document.documentElement.setAttribute('dir', langInfo?.dir || 'ltr');
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (SUPPORTED_LANGUAGES.find(l => l.code === code)) {
      localStorage.setItem(STORAGE_KEY, code);
      setLangState(code);
    }
  }, []);

  // Translation function — falls back to key if not found
  const t = useCallback((key, fallback) => {
    return translations[lang]?.[key] ?? translations['tr']?.[key] ?? fallback ?? key;
  }, [lang]);

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === lang);
  const isRTL = currentLang?.dir === 'rtl';

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isRTL, SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
